import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";

const testimonials = [
  {
    quote:
      "Deepshikha transformed our PMO from a reporting function into a true delivery engine. On-time delivery jumped from chaos to consistency within two quarters.",
    name: "Operations Director",
    role: "Global Industrial Manufacturer",
  },
  {
    quote:
      "The RFQ-to-order standardization she introduced cut our quoting cycle in half. Sales and engineering finally speak the same language.",
    name: "VP, Commercial Operations",
    role: "Energy Equipment Supplier",
  },
  {
    quote:
      "Calm under pressure, relentless on outcomes. The kind of consultant you wish you'd hired three years earlier.",
    name: "Program Lead",
    role: "EPC Major",
  },
];

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const next = () => setI((p) => (p + 1) % testimonials.length);
  const prev = () => setI((p) => (p - 1 + testimonials.length) % testimonials.length);

  return (
    <section id="testimonials" className="relative py-24 md:py-32 bg-[#0A192F]" data-testid="testimonials-section">
      <div className="px-6 md:px-12 lg:px-24">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="max-w-3xl mb-16"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-6">
            <div className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Testimonials</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#F8F9FA] leading-tight" data-testid="testimonials-heading">
            What clients say.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-3 text-xs text-[#A8B2D1] italic">
            * Placeholder quotes — replace with real testimonials.
          </motion.p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto" data-testid="testimonials-carousel">
          <Quote className="text-[#D4AF37]/30 mx-auto mb-8" size={48} strokeWidth={1} />
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <p className="font-serif text-2xl md:text-3xl text-[#F8F9FA] leading-relaxed italic text-balance">
                "{testimonials[i].quote}"
              </p>
              <div className="mt-10 flex flex-col items-center">
                <div className="gold-rule" />
                <p className="mt-4 font-serif text-lg text-[#D4AF37]">{testimonials[i].name}</p>
                <p className="text-xs uppercase tracking-[0.3em] text-[#A8B2D1] mt-1">{testimonials[i].role}</p>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-12 flex items-center justify-center gap-8">
            <button onClick={prev} className="text-[#A8B2D1] hover:text-[#D4AF37] transition-colors p-2" aria-label="Previous" data-testid="testimonial-prev">
              <ChevronLeft size={24} />
            </button>
            <div className="flex gap-3">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setI(idx)}
                  className={`h-1 transition-all duration-300 ${idx === i ? "w-10 bg-[#D4AF37]" : "w-5 bg-[#233554]"}`}
                  aria-label={`Go to testimonial ${idx + 1}`}
                  data-testid={`testimonial-dot-${idx}`}
                />
              ))}
            </div>
            <button onClick={next} className="text-[#A8B2D1] hover:text-[#D4AF37] transition-colors p-2" aria-label="Next" data-testid="testimonial-next">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
